import { adminUiCopy } from "@/copy/adminUiCopy";
import { ADMIN_PERMISSION_KEYS } from "@/routing/adminPermissionKeys";
import type {
  AdminV2RevenueMenuVisibility,
  AdminV2RevenueMenuVisibilityKey,
} from "@/types/adminV2";
import {
  DEFAULT_PUBLIC_MESSAGE,
  sanitizePublicMessage,
} from "@/utils/errors/publicErrorContract";

type ErrorRecord = Record<string, unknown>;

export const ROUTE_ACCESS_TIMEOUT_MESSAGE =
  adminUiCopy.routeAccess.timedOutMessage;
export const ROUTE_ACCESS_GENERIC_MESSAGE =
  adminUiCopy.routeAccess.unavailableMessage;
export const ROUTE_ACCESS_CHECK_FAILED_ERROR_CODE = "ACCESS_CHECK_FAILED";
export const ROUTE_ACCESS_CHECK_TIMEOUT_ERROR_CODE = "ACCESS_CHECK_TIMEOUT";

const isErrorRecord = (value: unknown): value is ErrorRecord =>
  Boolean(value) && typeof value === "object";

/**
 * Only an actual identity HTTP 403 establishes that this access was denied.
 *
 * CRITICAL (SLK-03 definite 403, Prism P55): a 403 from the identity check is a
 * definite refusal (for example a deactivated staff account), so the route
 * shows one permanent message with its code and correlation ID and no Retry.
 * Do not widen this to 5xx, 401, timeouts or an errorCode alone: those stay on
 * the transient retry copy. RFC 9110 says a client should not repeat a 403 with
 * the same credentials: https://www.rfc-editor.org/rfc/rfc9110#name-403-forbidden
 */
export const isForbiddenIdentityError = (error: unknown): boolean => {
  if (!isErrorRecord(error)) return false;
  return (
    error.status === 403 ||
    (isErrorRecord(error.response) && error.response.status === 403)
  );
};

const getStringValue = (
  record: ErrorRecord,
  key: string,
): string | undefined => {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const getErrorDetailMessage = (record: ErrorRecord): string | undefined => {
  const data = record.data;
  if (!isErrorRecord(data)) {
    return undefined;
  }

  const detail = data.detail;
  return typeof detail === "string" && detail.length > 0 ? detail : undefined;
};

/**
 * Formats an unknown API error into user-safe copy + optional correlation metadata.
 *
 * @remarks
 * Admin routes intentionally avoid showing raw internal errors. This helper:
 * - extracts a best-effort `correlationId` and `errorCode` when present
 * - sanitizes messages using the public error contract
 * - falls back to stable, generic route-check copy
 *
 * Callers should treat the returned fields as *safe to display* and prefer asking
 * support to investigate using the correlation id.
 *
 * @see docs/backend/readmes/PUBLIC_ERROR_CONTRACT.md
 * @see docs/LOGGING.md
 * @see apps/admin/src/utils/errors/publicErrorContract.ts
 */
export const formatApiError = (
  error: unknown,
): { message: string; correlation?: string; errorCode?: string } => {
  const safeRouteMessage = ROUTE_ACCESS_GENERIC_MESSAGE;
  const toSafeErrorCode = (value: string | undefined): string | undefined => {
    if (!value) {
      return undefined;
    }

    const normalized = value.trim();
    return /^[A-Za-z0-9._:-]{1,80}$/.test(normalized)
      ? normalized.toUpperCase()
      : undefined;
  };

  if (!isErrorRecord(error)) {
    return { message: safeRouteMessage };
  }

  const data = isErrorRecord(error.data) ? error.data : undefined;
  const rawCorrelation =
    getStringValue(error, "correlationId") ??
    (data ? getStringValue(data, "correlationId") : undefined);
  const correlation =
    rawCorrelation && /^[A-Za-z0-9._:-]{1,128}$/.test(rawCorrelation)
      ? rawCorrelation
      : undefined;
  const errorCode = toSafeErrorCode(
    getStringValue(error, "errorCode") ??
      (data ? getStringValue(data, "errorCode") : undefined) ??
      getStringValue(error, "code"),
  );
  const unsafeMessage =
    getStringValue(error, "message") ??
    getErrorDetailMessage(error) ??
    safeRouteMessage;
  const sanitizedMessage = sanitizePublicMessage(unsafeMessage);
  const message =
    sanitizedMessage === DEFAULT_PUBLIC_MESSAGE
      ? safeRouteMessage
      : sanitizedMessage;

  return { message, correlation, errorCode };
};

export const buildRouteAccessCheckFailureDetails = (args: {
  hasTimedOut: boolean;
  isIdentityAccessCheckPending: boolean;
  isVisibilityAccessCheckPending: boolean;
  identityError: unknown;
  visibilityError: unknown;
}): {
  identityMessage: string;
  visibilityMessage: string;
  errorCode: string;
  correlationId?: string;
} => {
  const permissionError = formatApiError(args.identityError);
  const visibilityError = formatApiError(args.visibilityError);

  const identityMessage =
    args.hasTimedOut && args.isIdentityAccessCheckPending
      ? ROUTE_ACCESS_TIMEOUT_MESSAGE
      : permissionError.message;
  const visibilityMessage =
    args.hasTimedOut && args.isVisibilityAccessCheckPending
      ? ROUTE_ACCESS_TIMEOUT_MESSAGE
      : visibilityError.message;

  return {
    identityMessage,
    visibilityMessage,
    errorCode: args.hasTimedOut
      ? ROUTE_ACCESS_CHECK_TIMEOUT_ERROR_CODE
      : (permissionError.errorCode ??
        visibilityError.errorCode ??
        ROUTE_ACCESS_CHECK_FAILED_ERROR_CODE),
    correlationId: permissionError.correlation ?? visibilityError.correlation,
  };
};

/**
 * Resolves the permission-key allowlist used by Admin E2E bypass mode.
 *
 * @remarks
 * When `adminE2eBypassEnabled` is on, route guards use an allowlist to preserve
 * "permission required" UX without depending on backend RBAC state. If no
 * allowlist is configured, we default to *all known* admin permission keys.
 *
 * @see apps/admin/src/routing/adminPermissionKeys.ts
 */
export const resolveBypassPermissionKeys = (
  configuredPermissionKeys: string[] | null,
): Set<string> =>
  new Set(configuredPermissionKeys ?? [...ADMIN_PERMISSION_KEYS]);

/**
 * Resolves the revenue menu-visibility object used by Admin E2E bypass mode.
 *
 * @remarks
 * In bypass mode we still respect menu visibility gates to prevent accidental
 * exposure of disabled modules during testing. If no config is provided, we
 * default everything to visible to avoid false negatives in local flows.
 */
export const resolveBypassMenuVisibility = (
  configuredMenuVisibility: AdminV2RevenueMenuVisibility | null | undefined,
): AdminV2RevenueMenuVisibility =>
  configuredMenuVisibility ?? {
    revenue: true,
    learning: true,
    sales: true,
  };

/**
 * Returns whether we should call the backend to check route permissions.
 *
 * @remarks
 * We only perform these checks when:
 * - the route declares a required permission, and
 * - Admin V2 foundations are enabled (kill-switch), and
 * - Admin E2E bypass mode is NOT enabled.
 */
export const shouldCheckPermission = (
  requiredPermission: string | undefined,
  adminV2FoundationsEnabled: boolean,
  adminE2eBypassEnabled: boolean,
): boolean =>
  Boolean(requiredPermission) &&
  adminV2FoundationsEnabled &&
  !adminE2eBypassEnabled;

/**
 * Returns whether we should call the backend to check route menu visibility.
 *
 * @remarks
 * Visibility gates are handled the same way as permission gates: skip calls
 * when Admin V2 is kill-switched off or when E2E bypass mode is enabled.
 */
export const shouldCheckMenuVisibility = (
  requiredMenuVisibility: AdminV2RevenueMenuVisibilityKey | undefined,
  adminV2FoundationsEnabled: boolean,
  adminE2eBypassEnabled: boolean,
): boolean =>
  Boolean(requiredMenuVisibility) &&
  adminV2FoundationsEnabled &&
  !adminE2eBypassEnabled;
